import { useEffect, useState } from "react";
import { adminApi } from "../../api/adminApi";
import { DataTable } from "../../components/DataTable";
import type { MpesaConfigDto, PaymentTransaction, RevenueOverview, TenantUsage } from "../../api/types";

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="stat-card">
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
    </div>
  );
}

function MpesaConfigPanel() {
  const [config, setConfig] = useState<MpesaConfigDto | null>(null);
  const [consumerKey, setConsumerKey] = useState("");
  const [consumerSecret, setConsumerSecret] = useState("");
  const [shortcode, setShortcode] = useState("");
  const [passkey, setPasskey] = useState("");
  const [baseUrl, setBaseUrl] = useState("https://sandbox.safaricom.co.ke");
  const [callbackSecret, setCallbackSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    setLoadError(null);
    adminApi.platform.mpesaConfig
      .get()
      .then((c) => {
        setConfig(c);
        setConsumerKey(c.consumerKey ?? "");
        setShortcode(c.shortcode ?? "");
        setBaseUrl(c.baseUrl);
        setCallbackSecret(c.callbackSecret ?? "");
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : "Failed to load M-Pesa config"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      const saved = await adminApi.platform.mpesaConfig.save({
        consumerKey: consumerKey.trim() || null,
        consumerSecret: consumerSecret.trim() ? consumerSecret.trim() : null,
        shortcode: shortcode.trim() || null,
        passkey: passkey.trim() ? passkey.trim() : null,
        baseUrl: baseUrl.trim() || null,
        callbackSecret: callbackSecret.trim() || null,
      });
      setConfig(saved);
      setConsumerSecret("");
      setPasskey("");
      setMessage("Saved.");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p>Loading...</p>;

  return (
    <div className="page-panel">
      <h3>M-Pesa (Daraja) configuration</h3>
      {loadError && <div className="error-text">{loadError}</div>}
      <p className="help-text">
        Your own Safaricom paybill/till - tenants pay their ReportForge subscription into this.
        Use the sandbox base URL while testing.
      </p>

      <div className="form-grid">
        <div className="form-row">
          <label>Consumer key</label>
          <input value={consumerKey} onChange={(e) => setConsumerKey(e.target.value)} />
        </div>
        <div className="form-row">
          <label>Consumer secret {config?.hasConsumerSecret && <span className="help-text">(set)</span>}</label>
          <input
            type="password"
            value={consumerSecret}
            onChange={(e) => setConsumerSecret(e.target.value)}
            placeholder={config?.hasConsumerSecret ? "•••••••• (unchanged)" : ""}
          />
        </div>
        <div className="form-row">
          <label>Shortcode</label>
          <input value={shortcode} onChange={(e) => setShortcode(e.target.value)} placeholder="174379" />
        </div>
        <div className="form-row">
          <label>Passkey {config?.hasPasskey && <span className="help-text">(set)</span>}</label>
          <input
            type="password"
            value={passkey}
            onChange={(e) => setPasskey(e.target.value)}
            placeholder={config?.hasPasskey ? "•••••••• (unchanged)" : ""}
          />
        </div>
        <div className="form-row">
          <label>Base URL</label>
          <select value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}>
            <option value="https://sandbox.safaricom.co.ke">Sandbox</option>
            <option value="https://api.safaricom.co.ke">Production</option>
          </select>
        </div>
        <div className="form-row">
          <label>Callback secret</label>
          <input value={callbackSecret} onChange={(e) => setCallbackSecret(e.target.value)} />
        </div>
      </div>

      {config?.callbackUrlHint && (
        <div className="form-row" style={{ maxWidth: "none" }}>
          <label>Callback URL (give this to Safaricom / set in your Daraja app)</label>
          <div className="code-block">{config.callbackUrlHint}</div>
        </div>
      )}

      <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? "Saving..." : "Save"}
      </button>
      {message && <span className="help-text" style={{ marginLeft: 10 }}>{message}</span>}
    </div>
  );
}

function ManualPaymentPanel({ onRecorded }: { onRecorded: () => void }) {
  const [tenants, setTenants] = useState<TenantUsage[]>([]);
  const [tenantId, setTenantId] = useState("");
  const [planCode, setPlanCode] = useState("starter");
  const [amountKes, setAmountKes] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    adminApi.platform
      .tenants()
      .then(setTenants)
      .catch((err) => setMessage(err instanceof Error ? err.message : "Failed to load tenants"));
  }, []);

  async function handleRecord() {
    if (!tenantId) return;
    setSaving(true);
    setMessage(null);
    try {
      await adminApi.platform.recordManualPayment(tenantId, {
        planCode,
        amountKes: amountKes.trim() ? Number(amountKes) : null,
        note: note.trim() || null,
      });
      setMessage("Payment recorded and subscription extended.");
      setNote("");
      onRecorded();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to record payment");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page-panel">
      <h3>Record a manual payment</h3>
      <p className="help-text">Bank transfer, cash, or any payment collected outside M-Pesa.</p>
      <div className="form-grid">
        <div className="form-row">
          <label>Tenant</label>
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">Select a tenant...</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label>Plan</label>
          <select value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="business">Business</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div className="form-row">
          <label>Amount (KES, optional — defaults to plan price)</label>
          <input value={amountKes} onChange={(e) => setAmountKes(e.target.value)} type="number" />
        </div>
        <div className="form-row">
          <label>Note</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Bank ref #..." />
        </div>
      </div>
      <button type="button" className="btn-primary" onClick={handleRecord} disabled={saving || !tenantId}>
        {saving ? "Recording..." : "Record payment"}
      </button>
      {message && <span className="help-text" style={{ marginLeft: 10 }}>{message}</span>}
    </div>
  );
}

export default function Revenue() {
  const [overview, setOverview] = useState<RevenueOverview | null>(null);
  const [payments, setPayments] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    setError(null);
    Promise.all([adminApi.platform.revenue(), adminApi.platform.payments()])
      .then(([o, p]) => {
        setOverview(o);
        setPayments(p.content);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load revenue data"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  return (
    <div>
      <div className="page-header">
        <h1>Revenue</h1>
      </div>

      {error && <div className="error-text">{error}</div>}

      {loading ? (
        <p>Loading...</p>
      ) : (
        overview && (
          <div className="stat-grid">
            <StatCard label="Total revenue (KES)" value={overview.totalRevenueKes.toLocaleString()} />
            <StatCard label="This month (KES)" value={overview.revenueThisMonthKes.toLocaleString()} />
            <StatCard label="Successful payments" value={overview.successfulPayments} />
            <StatCard label="Pending" value={overview.pendingPayments} />
          </div>
        )
      )}

      <MpesaConfigPanel />
      <ManualPaymentPanel onRecorded={load} />

      <div className="page-panel">
        <h3>Recent payments</h3>
        <DataTable
          rows={payments}
          rowKey={(p) => p.id}
          emptyMessage="No payments yet."
          columns={[
            { key: "planCode", header: "Plan" },
            { key: "amountKes", header: "Amount (KES)", render: (p) => p.amountKes.toLocaleString() },
            { key: "method", header: "Method" },
            { key: "status", header: "Status" },
            { key: "createdAt", header: "Date", render: (p) => new Date(p.createdAt).toLocaleString() },
          ]}
        />
      </div>
    </div>
  );
}
